import type { RJSFSchema } from '@rjsf/utils';

import { ROOM_RATE_FLASH_TYPE } from '~/consts/ratePlans';

export const DEFAULT_FLASH_TAX_FEE_PRODUCT_TYPE = ROOM_RATE_FLASH_TYPE;

type TaxRule = {
  amount?: number;
  currency?: string;
  name: string;
  rate?: number;
};

type TaxFeeFormItem = {
  name: string;
  unit: {
    amount?: number;
    currency?: string;
    value?: number;
  };
  type: string;
  per_person: boolean;
  product_type: string;
  payable_at_property: boolean;
  additional_tax: boolean;
  exclude_from_cm_rate: boolean;
  exclude_flash_pay_at_property: boolean;
};

export const TAX_BREAKDOWN_SOURCE_URL =
  'https://aussiecommerce.atlassian.net/wiki/spaces/SO/pages/2289500200/Tax+Breakdown+by+Country';

const TAX_RULES_BY_COUNTRY: Record<string, Array<TaxRule>> = {
  fiji: [{ name: 'VAT', rate: 15 }],
  maldives: [
    { name: 'GST', rate: 17 },
    { name: 'Service Fee', rate: 10 },
  ],
  indonesia: [
    { name: 'Gov Tax', rate: 10 },
    { name: 'Service Fee', rate: 11 },
  ],
  thailand: [
    { name: 'VAT', rate: 7 },
    { name: 'Service Charge', rate: 10 },
  ],
  mexico: [
    { name: 'VAT', rate: 16 },
    { name: 'Occupancy', rate: 3 },
    { name: 'Service fee', rate: 15 },
  ],
  'united kingdom': [{ name: 'VAT', rate: 15 }],
  vanuatu: [{ name: 'VAT', rate: 15 }],
  vietnam: [
    { name: 'VAT', rate: 10 },
    { name: 'Service Charge', rate: 5 },
  ],
  malaysia: [{ amount: 10, currency: 'MYR', name: 'Tourism Fee' }],
};

const TAX_RULES_BY_CITY: Record<string, Array<TaxRule>> = {
  dubai: [
    { name: 'Municipality Fee', rate: 7 },
    { name: 'Service Charge', rate: 10 },
    { name: 'VAT', rate: 5 },
    { amount: 20, currency: 'AED', name: 'Tourism Fee' },
  ],
};

const KOH_SAMUI_RULES: Array<TaxRule> = [...TAX_RULES_BY_COUNTRY.thailand, { name: 'Local/city tax', rate: 1 }];

function normalize(value?: string | null) {
  return value?.trim().toLowerCase() ?? '';
}

function toTaxFeeFormItem(rule: TaxRule): TaxFeeFormItem {
  return {
    name: rule.name,
    unit:
      typeof rule.amount === 'number'
        ? {
            amount: rule.amount,
            currency: rule.currency,
          }
        : {
            value: rule.rate,
          },
    type: 'night',
    per_person: false,
    product_type: DEFAULT_FLASH_TAX_FEE_PRODUCT_TYPE,
    payable_at_property: false,
    additional_tax: false,
    exclude_from_cm_rate: false,
    exclude_flash_pay_at_property: false,
  };
}

export function withDefaultTaxFeeProductType<T extends RJSFSchema>(schema: T): T {
  const items = schema.items;

  if (!items || typeof items !== 'object' || Array.isArray(items)) {
    return schema;
  }

  const productType = items.properties?.product_type;

  if (!productType || typeof productType !== 'object' || Array.isArray(productType)) {
    return schema;
  }

  return {
    ...schema,
    items: {
      ...items,
      properties: {
        ...items.properties,
        product_type: {
          ...productType,
          default: DEFAULT_FLASH_TAX_FEE_PRODUCT_TYPE,
        },
      },
    },
  };
}

export function getTaxFeePrefillItems(country?: string | null, city?: string | null, locationText?: string | null) {
  const normalizedCountry = normalize(country);
  const normalizedCity = normalize(city);
  const normalizedLocationText = normalize(locationText);

  if (!normalizedCountry && !normalizedCity && !normalizedLocationText) {
    return [];
  }

  if (
    normalizedCountry === 'thailand' &&
    (normalizedCity.includes('koh samui') || normalizedLocationText.includes('koh samui'))
  ) {
    return KOH_SAMUI_RULES.map(toTaxFeeFormItem);
  }

  const matchingCity = Object.keys(TAX_RULES_BY_CITY).find((cityKey) => normalizedCity.includes(cityKey));

  if (matchingCity) {
    return TAX_RULES_BY_CITY[matchingCity].map(toTaxFeeFormItem);
  }

  const matchingCountry =
    Object.keys(TAX_RULES_BY_COUNTRY).find((countryKey) => normalizedCountry === countryKey) ??
    Object.keys(TAX_RULES_BY_COUNTRY).find((countryKey) => normalizedLocationText.includes(countryKey));

  if (matchingCountry) {
    return TAX_RULES_BY_COUNTRY[matchingCountry].map(toTaxFeeFormItem);
  }

  return [];
}
